/**
 * DataImportSection — Settings → Data → Import.
 *
 * Multi-step wizard: choose PMS type → upload → preview & review → commit.
 * Everything is a single React state machine driven by the job status
 * returned from the backend. The user can leave and come back — we always
 * fetch the latest job list on mount and offer to resume.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Loader2, FileArchive, Database, RefreshCcw, CheckCircle2,
  AlertTriangle, XCircle, Trash2, ChevronRight, FileText, Image as ImageIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { SectionHeader, SettingCard } from "./SettingsLayout";

const SOURCE_TYPES = [
  { key: "practo",    title: "Practo",     desc: "CSV patient export." },
  { key: "dentrix",   title: "Dentrix",    desc: "XML patient export." },
  { key: "carestack", title: "CareStack",  desc: "CSV or JSON export." },
  { key: "generic",   title: "Generic CSV", desc: "Any PMS — map columns yourself." },
];

const STATUS_META = {
  created:          { label: "Ready to upload", tone: "amber" },
  uploading:        { label: "Uploading",       tone: "amber" },
  parsing:          { label: "Parsing",         tone: "amber" },
  ready_to_commit:  { label: "Ready to commit", tone: "green" },
  committing:       { label: "Committing",      tone: "amber" },
  committed:        { label: "Committed",       tone: "green" },
  failed:           { label: "Failed",          tone: "red" },
  cancelled:        { label: "Cancelled",       tone: "grey" },
};

const TONE_CLASS = {
  amber: "bg-[#FDF3ED] text-[#8A3F1E]",
  green: "bg-[#E8F0EB] text-[#2D6A4F]",
  red:   "bg-[#FCECEC] text-[#B94A48]",
  grey:  "bg-[#EDEDED] text-[#6B7280]",
};

export default function DataImportSection() {
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadJobs = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/migrations");
      setJobs(data.jobs || []);
    } catch (e) {
      toast.error("Could not load migration jobs");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const startNew = async (sourceType) => {
    setCreating(true);
    try {
      const { data } = await api.post("/migrations", { source_type: sourceType });
      setActiveJob(data);
      await loadJobs();
    } catch (e) {
      toast.error(apiError(e, "Could not create migration job"));
    } finally {
      setCreating(false);
    }
  };

  const openJob = async (job) => {
    const { data } = await api.get(`/migrations/${job.id}`);
    setActiveJob(data.job);
  };

  const closeJob = () => { setActiveJob(null); loadJobs(); };

  return (
    <div data-testid="data-import-section">
      <SectionHeader
        title="Import from another PMS"
        description="Move patients, treatment history, and imaging files from Practo, Dentrix, CareStack or any generic CSV/ZIP export. Every file lands inside the correct patient's Imaging &amp; Documents section — nothing gets mixed up."
      />

      {activeJob ? (
        <ActiveJobPane job={activeJob} onClose={closeJob} onRefresh={openJob} />
      ) : (
        <>
          <SettingCard
            title="Start a new import"
            description="Pick the platform your existing data is coming from. We'll parse it, show you a preview, and only commit records once you approve."
            dataTestId="start-new-import">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SOURCE_TYPES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`source-${s.key}`}
                  onClick={() => startNew(s.key)}
                  disabled={creating}
                  className="text-left rounded-2xl border border-[#EFEADC] bg-linen/50 hover:bg-white hover:border-pine/30 hover:shadow-sm transition p-5 disabled:opacity-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-pine/10 text-pine grid place-items-center">
                      <Database className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg text-ink">{s.title}</div>
                      <div className="text-sm text-ink-mid mt-0.5">{s.desc}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-ink-soft mt-2" />
                  </div>
                </button>
              ))}
            </div>
          </SettingCard>

          <SettingCard
            title="Recent imports"
            description="Historical jobs — click any to resume, review or clean up."
            dataTestId="recent-imports">
            <RecentJobsList
              jobs={jobs} loading={loadingList} onOpen={openJob} onReload={loadJobs}
            />
          </SettingCard>
        </>
      )}
    </div>
  );
}


/* ---------------------------------------------------------------- Jobs list */
function RecentJobsList({ jobs, loading, onOpen, onReload }) {
  if (loading) return <div className="py-6 text-center text-ink-soft"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  if (jobs.length === 0) {
    return <div className="py-6 text-center text-sm text-ink-soft">No imports yet.</div>;
  }
  return (
    <div className="space-y-2">
      {jobs.map((j) => {
        const meta = STATUS_META[j.status] || { label: j.status, tone: "grey" };
        return (
          <button
            key={j.id}
            data-testid={`job-${j.id}`}
            onClick={() => onOpen(j)}
            className="w-full text-left rounded-xl border border-[#EFEADC] bg-white hover:bg-linen/50 px-4 py-3 flex items-center gap-4 transition">
            <FileArchive className="w-5 h-5 text-pine shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink truncate">
                {j.label || j.source_type}{" "}
                <span className="text-xs text-ink-soft ml-1">({j.source_type})</span>
              </div>
              <div className="text-xs text-ink-soft truncate">
                {j.source_name || "no file uploaded"} · {new Date(j.created_at).toLocaleString()}
              </div>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${TONE_CLASS[meta.tone]}`}>
              {meta.label}
            </span>
            <ChevronRight className="w-4 h-4 text-ink-soft" />
          </button>
        );
      })}
      <div className="pt-2">
        <button data-testid="refresh-jobs" onClick={onReload}
                className="text-xs text-ink-soft hover:text-ink inline-flex items-center gap-1.5">
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>
    </div>
  );
}


/* ---------------------------------------------------------------- Active job */
function ActiveJobPane({ job: initialJob, onClose, onRefresh }) {
  const [job, setJob] = useState(initialJob);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [committing, setCommitting] = useState(false);
  const [forceConflicts, setForceConflicts] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const refetch = useCallback(async () => {
    const { data } = await api.get(`/migrations/${job.id}`);
    setJob(data.job);
    return data.job;
  }, [job.id]);

  // Poll while parsing / committing.
  useEffect(() => {
    if (["parsing", "committing", "uploading"].includes(job.status)) {
      pollRef.current = setInterval(refetch, 1500);
      return () => clearInterval(pollRef.current);
    }
  }, [job.status, refetch]);

  const upload = async (file) => {
    setUploading(true); setUploadPct(0);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/migrations/${job.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Uploaded — parsing in the background");
      await refetch();
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this import? Staged records will be discarded.")) return;
    try {
      await api.delete(`/migrations/${job.id}`);
      toast.success("Import cancelled");
      onClose();
    } catch (e) {
      toast.error("Cancel failed");
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/migrations/${job.id}/commit`,
                                       { force_conflicts: forceConflicts });
      toast.success(
        `Imported ${data.counts.patients_created} patients & ${data.counts.files_uploaded} files`,
      );
      await refetch();
    } catch (e) {
      toast.error(apiError(e, "Commit failed"));
    } finally {
      setCommitting(false);
    }
  };

  const meta = STATUS_META[job.status] || { label: job.status, tone: "grey" };
  const canUpload = job.status === "created" || job.status === "failed";
  const canCommit = job.status === "ready_to_commit";

  return (
    <div data-testid="active-import-pane">
      <div className="flex items-center gap-3 mb-4">
        <button
          data-testid="back-to-list"
          onClick={onClose}
          className="text-sm text-ink-soft hover:text-ink inline-flex items-center gap-1">
          ← All imports
        </button>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${TONE_CLASS[meta.tone]}`}>
          {meta.label}
        </span>
      </div>

      <SettingCard
        title={job.label || `${job.source_type} import`}
        description={
          job.source_name
            ? `${job.source_name} · ${formatBytes(job.source_size || 0)}`
            : "Awaiting file upload"
        }
        dataTestId="job-header">
        {job.error && (
          <div className="rounded-xl bg-[#FCECEC] text-[#B94A48] px-4 py-3 text-sm mb-4 flex items-start gap-2">
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div><strong>Failed:</strong> {job.error}</div>
          </div>
        )}

        {canUpload && (
          <div>
            <input
              ref={fileRef}
              data-testid="upload-input"
              type="file"
              accept=".zip,.csv,.xml,.json,.tsv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            />
            <button
              data-testid="upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-pine text-linen text-sm hover:bg-pine/90 disabled:opacity-50">
              {uploading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {uploadPct}%</>
                : <><Upload className="w-4 h-4" /> Choose ZIP / CSV / XML / JSON</>}
            </button>
            <div className="text-xs text-ink-soft mt-2">Max 500 MB · ZIP may contain images (JPG, PNG, DICOM, PDF)</div>
          </div>
        )}

        {job.status === "parsing" && (
          <div className="text-sm text-ink-mid inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Parsing your file — hold tight
          </div>
        )}

        {(job.status === "ready_to_commit" || job.status === "committed") && (
          <JobPreview
            job={job}
            forceConflicts={forceConflicts}
            setForceConflicts={setForceConflicts}
            onRefresh={refetch}
          />
        )}
      </SettingCard>

      {canCommit && (
        <div className="flex items-center justify-between rounded-2xl bg-pine text-linen p-5">
          <div>
            <div className="font-display text-lg">Ready to commit</div>
            <div className="text-sm text-linen/70">
              {job.counts?.patients_ready || 0} patients &amp; {job.counts?.files_ready || 0} files will be written to your database.
              {job.counts?.files_review > 0 && ` ${job.counts.files_review} files still need review.`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cancel-btn" onClick={cancel}
                    className="px-3 py-2 rounded-full border border-linen/25 text-sm hover:bg-linen/10">
              <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Discard
            </button>
            <button data-testid="commit-btn" onClick={commit} disabled={committing}
                    className="px-4 py-2 rounded-full bg-linen text-ink text-sm font-medium hover:bg-white disabled:opacity-60">
              {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}
              {committing ? "Committing…" : "Commit import"}
            </button>
          </div>
        </div>
      )}

      {job.status === "committed" && (
        <div className="rounded-2xl bg-[#E8F0EB] text-[#2D6A4F] p-5 mt-4" data-testid="committed-summary">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5" />
            <div>
              <div className="font-display text-lg">Import complete</div>
              <div className="text-sm">
                {job.counts_committed?.patients_created || 0} patients created,
                {" "}{job.counts_committed?.files_uploaded || 0} files uploaded.
                {job.counts_committed?.files_needs_review > 0 &&
                  ` ${job.counts_committed.files_needs_review} files remain in the review queue.`}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ---------------------------------------------------------------- Preview + review */
function JobPreview({ job, forceConflicts, setForceConflicts, onRefresh }) {
  const [patients, setPatients] = useState([]);
  const [reviewFiles, setReviewFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p1, p2] = await Promise.all([
        api.get(`/migrations/${job.id}/preview?type=patient&limit=200`),
        api.get(`/migrations/${job.id}/preview?type=file&status=needs_review&limit=200`),
      ]);
      setPatients(p1.data.records || []);
      setReviewFiles(p2.data.records || []);
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { load(); }, [load]);

  const conflicts = patients.filter((p) => p.status === "conflict");
  const ready = patients.filter((p) => p.status === "ready");

  return (
    <div className="space-y-6" data-testid="job-preview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip label="Patients (new)"   value={ready.length}      testid="stat-ready" />
        <StatChip label="Duplicates"       value={conflicts.length}  tone="amber" testid="stat-conflict" />
        <StatChip label="Files linked"     value={job.counts?.files_ready || 0} testid="stat-files-ok" />
        <StatChip label="Files to review"  value={reviewFiles.length} tone="amber" testid="stat-files-review" />
      </div>

      {loading && <div className="text-sm text-ink-soft"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading preview…</div>}

      {conflicts.length > 0 && (
        <details className="rounded-xl border border-[#F5E0D2] bg-[#FDF9F0] p-4" open>
          <summary className="cursor-pointer text-sm font-medium text-ink flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#B0521F]" />
            {conflicts.length} duplicate {conflicts.length === 1 ? "patient" : "patients"} detected
          </summary>
          <div className="text-xs text-ink-mid mt-2 mb-3">
            These records look like they already exist in your database.
            <label className="ml-2 inline-flex items-center gap-1.5 cursor-pointer">
              <input
                data-testid="force-conflicts"
                type="checkbox"
                checked={forceConflicts}
                onChange={(e) => setForceConflicts(e.target.checked)}
              />
              <span>Attach ZIP files to the existing patient and skip re-creating</span>
            </label>
          </div>
          <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
            {conflicts.slice(0, 30).map((c) => (
              <li key={c.id} className="flex justify-between gap-3 py-1 border-b border-[#EFEADC] last:border-b-0">
                <span className="truncate">
                  <strong>{c.payload.name}</strong> · {c.payload.phone || "—"} · {c.payload.email || "—"}
                </span>
                <span className="text-xs text-[#B0521F] shrink-0">
                  {(c.match_reason || []).join(", ") || "match"}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <details className="rounded-xl border border-[#EFEADC] bg-white p-4">
        <summary className="cursor-pointer text-sm font-medium text-ink flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Preview {ready.length} new patients
        </summary>
        <div className="mt-3 max-h-72 overflow-y-auto text-sm">
          <table className="w-full text-left">
            <thead className="text-[10px] uppercase tracking-widest text-ink-soft">
              <tr><th className="py-2">Name</th><th>Phone</th><th>DOB</th><th>Gender</th></tr>
            </thead>
            <tbody>
              {ready.slice(0, 100).map((r) => (
                <tr key={r.id} className="border-t border-[#EFEADC]">
                  <td className="py-1.5">{r.payload.name}</td>
                  <td className="text-ink-mid">{r.payload.phone || "—"}</td>
                  <td className="text-ink-mid">{r.payload.dob || "—"}</td>
                  <td className="text-ink-mid">{r.payload.gender || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {reviewFiles.length > 0 && (
        <ReviewFilesPanel jobId={job.id} files={reviewFiles} onReviewed={() => { load(); onRefresh(); }} />
      )}
    </div>
  );
}

function StatChip({ label, value, tone = "grey", testid }) {
  return (
    <div className={`rounded-xl border p-3 ${
      tone === "amber" ? "bg-[#FDF3ED] border-[#F5E0D2] text-[#8A3F1E]" : "bg-white border-[#EFEADC]"
    }`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}


/* --------------------------------------------------- Review queue panel */
function ReviewFilesPanel({ jobId, files, onReviewed }) {
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/patients?limit=200`);
      const list = Array.isArray(data) ? data : (data.patients || []);
      setPatients(list);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return patients.slice(0, 40);
    const needle = q.trim().toLowerCase();
    return patients.filter((p) =>
      (p.name || "").toLowerCase().includes(needle) ||
      (p.phone || "").includes(needle),
    ).slice(0, 40);
  }, [patients, q]);

  const assign = async (fileId, patientId) => {
    setProcessing(fileId);
    try {
      await api.post(`/migrations/${jobId}/records/${fileId}/review`,
                     { action: "assign", patient_id: patientId });
      toast.success("Linked");
      onReviewed?.();
    } catch (e) {
      toast.error("Could not link file");
    } finally {
      setProcessing(null);
    }
  };

  const skip = async (fileId) => {
    setProcessing(fileId);
    try {
      await api.post(`/migrations/${jobId}/records/${fileId}/review`,
                     { action: "skip" });
      onReviewed?.();
    } catch (e) {
      toast.error("Could not skip file");
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="rounded-xl border border-[#F5E0D2] bg-[#FDF9F0] p-4" data-testid="review-panel">
      <div className="text-sm font-medium text-ink flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-[#B0521F]" />
        {files.length} file{files.length === 1 ? "" : "s"} need review
      </div>
      <div className="text-xs text-ink-mid mb-3">
        These files couldn't be linked to a patient automatically. Search for the patient and click "Assign", or skip.
      </div>
      <input
        data-testid="review-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search patient by name or phone…"
        className="w-full rounded-lg border border-[#E7E1D3] px-3 py-2 text-sm mb-3 bg-white"
      />

      <ul className="space-y-2 max-h-96 overflow-y-auto">
        {files.map((f) => (
          <li key={f.id} className="rounded-lg bg-white border border-[#EFEADC] p-3">
            <div className="flex items-center gap-3">
              <ImageIcon className="w-4 h-4 text-pine shrink-0" />
              <div className="text-sm flex-1 min-w-0">
                <div className="font-medium truncate">{f.payload.filename}</div>
                <div className="text-xs text-ink-soft truncate">
                  {f.payload.original_path} · {formatBytes(f.payload.size)} · {f.link_reason}
                </div>
              </div>
              <button
                data-testid={`skip-${f.id}`}
                onClick={() => skip(f.id)}
                disabled={processing === f.id}
                className="text-xs text-ink-soft hover:text-ink px-2 py-1 rounded border border-[#E7E1D3]">
                Skip
              </button>
            </div>
            <div className="mt-2 pl-7">
              <div className="flex flex-wrap gap-1.5">
                {filtered.slice(0, 6).map((p) => (
                  <button
                    key={p.id}
                    data-testid={`assign-${f.id}-${p.id}`}
                    onClick={() => assign(f.id, p.id)}
                    disabled={processing === f.id}
                    className="text-xs px-2 py-1 rounded-full bg-pine/10 text-ink hover:bg-pine/20">
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


/* --------------------------------------------------- utils */
function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v > 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
